<template>
  <div class="sidebar-left">
    <div class="sidebar">
      <div class="sidebar-content" :class="{ 'show': mqShallShowLeftSidebar }" style="position: relative;">

        <!-- Header -->
        <div class="chat-fixed-search">
          <b-input-group class="input-group-merge w-100 round">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" class="text-muted" />
            </b-input-group-prepend>
            <b-form-input v-model="searchQuery" placeholder="Rechercher..." @input="debouncedSearch" />
            <b-input-group-append is-text>
              <feather-icon
                id="search-help-icon"
                icon="HelpCircleIcon"
                class="text-muted cursor-pointer search-help-icon"
                size="16"
              />
              <b-popover target="search-help-icon" triggers="hover focus" placement="bottom" custom-class="search-help-popover">
                <ul class="search-help-list">
                  <li><strong>Texte</strong> : nom, ville, email</li>
                  <li><strong>Tranche d'âge</strong> : !under 14, !14-17, !18-25, !26-30, ...</li>
                  <li><strong>N° de téléphone</strong> : +33612345678, 5678</li>
                  <li><strong>ID</strong> : #133</li>
                </ul>
              </b-popover>
            </b-input-group-append>
          </b-input-group>

          <!-- Dropdown Filter -->
          <b-dropdown dropright variant="link" no-caret>
            <template #button-content>
              <feather-icon icon="FilterIcon" :class="{ 'text-muted': !isFiltering, 'text-primary': isFiltering }" size="20" />
            </template>
            <b-dropdown-text>Filtrer par</b-dropdown-text>
            <b-dropdown-item class="w-100 text-left" :active="isFilteringByAbsencesCalls" @click="filterByAbsencesCalls">
              <feather-icon icon="PhoneMissedIcon" class="mr-1" size="17" />
              Appels en Absence
            </b-dropdown-item>
            <b-dropdown-item v-if="showMessagingFilter" class="w-100 text-left" :active="isFilteringByPendingConversation" @click="filterByPendingConversation">
              <feather-icon icon="ClockIcon" class="mr-1" size="17" />
              SMS en attente de prise en charge
            </b-dropdown-item>
            <b-dropdown-divider v-if="isFiltering" />
            <b-dropdown-item v-if="isFiltering" class="w-100 text-left" @click="clearFilters">
              <feather-icon icon="XIcon" class="mr-1" size="17" />
              Effacer les filtres
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <GlobalSpinner :is-loading="isLoading" />

        <!-- Youngs -->
        <vue-perfect-scrollbar :settings="perfectScrollbarSettings"
                               class="chat-user-list-wrapper list-group scroll-area" @ps-y-reach-end="onScrollToBottom">
          <ul class="list-unstyled chat-users-list">
            <young-contact
              v-for="(young, index) in youngs"
              :key="young.id"
              :young="young"
              :class="[
                { 'active': activeChatYoungId === young.id },
                { 'archived': youngs[index] && youngs[index].conversationStatus === 3 },
                { 'has-unread': youngs[index] && youngs[index].unreadMessages > 0 }
              ]"
              tag="li"
              @click="$emit('open-chat', young.id)"
            />
          </ul>
          <div v-if="youngs.length === 0 && !isLoading" class="empty-state p-2 text-center text-muted">
            Aucun résultat trouvé
          </div>
        </vue-perfect-scrollbar>
      </div>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import {
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupAppend,
  BFormInput,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BDropdownText,
  BPopover,
} from 'bootstrap-vue'
import { computed, watch, ref } from '@vue/composition-api'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'

import YoungContact from './YoungContact.vue'

export default {
  components: {
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BFormInput,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BDropdownText,
    BPopover,
    VuePerfectScrollbar,
    YoungContact,
  },
  props: {
    youngs: {
      type: Array,
      required: true,
    },
    activeChatYoungId: {
      type: Number,
      default: null,
    },
    mqShallShowLeftSidebar: {
      type: Boolean,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    paginationInfo: {
      type: Object,
      // Exécuté seulement si pas de valeur transmise par le parent
      default: () => ({
        currentPage: 1,
        pageSize: 50,
        hasNext: false,
        hasPrevious: false,
      }),
    },
  },
  setup(props, { emit }) {
    const debounceTimeout = ref(null)
    const users = ref(computed(() => store.getters['users/users']))

    if (users.value.length === 0) {
      store.dispatch('youngs/fetchLastContacts')
    }

    const perfectScrollbarSettings = {
      maxScrollbarLength: 150,
    }

    watch(() => props.youngs, newYoungs => {
      const needsFetching = newYoungs.some(young => young && !young.lastMessageUser && young.lastMessageUserId)
      if (needsFetching) {
        store.dispatch('youngs/fetchLastContacts')
      }
    }, { immediate: true })

    const currentUser = computed(() => store.getters['users/currentUser'])
    const currentUserRoles = computed(() => currentUser.value?.roles || [])
    const isEcoutant = computed(() => currentUserRoles.value.includes('LeRefuge.Ecoutant'))
    const showMessagingFilter = computed(
      () => !isEcoutant.value || currentUser.value?.profile?.canSendMessage === true,
    )

    const isFiltering = ref(false)
    const isFilteringByAbsencesCalls = ref(false)
    const isFilteringByPendingConversation = ref(false)

    function updateFilterIcon() {
      isFiltering.value = isFilteringByAbsencesCalls.value || isFilteringByPendingConversation.value
    }

    // Search handling with debounce
    const searchQuery = ref('')
    function debouncedSearch() {
      clearTimeout(debounceTimeout.value)
      debounceTimeout.value = setTimeout(() => {
        emit('search', {
          searchQuery: searchQuery.value,
          isFilteringByAbsencesCalls: isFilteringByAbsencesCalls.value,
          isFilteringByPendingConversation: isFilteringByPendingConversation.value,
          page: 1,
        })
      }, 700)
    }

    function toggleFilterIcon() {
      isFiltering.value = !isFiltering.value
    }

    async function filterByAbsencesCalls() {
      isFilteringByAbsencesCalls.value = !isFilteringByAbsencesCalls.value
      isFilteringByPendingConversation.value = false
      updateFilterIcon()

      emit('search', {
        searchQuery: searchQuery.value,
        isFilteringByAbsencesCalls: isFilteringByAbsencesCalls.value,
        isFilteringByPendingConversation: false,
        page: 1,
      })
    }

    async function filterByPendingConversation() {
      isFilteringByPendingConversation.value = !isFilteringByPendingConversation.value
      isFilteringByAbsencesCalls.value = false
      updateFilterIcon()

      emit('search', {
        searchQuery: searchQuery.value,
        isFilteringByAbsencesCalls: false,
        isFilteringByPendingConversation: isFilteringByPendingConversation.value,
        page: 1,
      })
    }

    function clearFilters() {
      isFilteringByAbsencesCalls.value = false
      isFilteringByPendingConversation.value = false
      isFiltering.value = false

      emit('search', {
        searchQuery: searchQuery.value,
        isFilteringByAbsencesCalls: false,
        isFilteringByPendingConversation: false,
        page: 1,
      })
    }

    function onScrollToBottom() {
      if (props.paginationInfo.hasNext && !props.isLoading) {
        emit('loadMore')
      }
    }

    return {
      perfectScrollbarSettings,

      // Search
      debouncedSearch,
      searchQuery,

      // Filter
      toggleFilterIcon,
      filterByAbsencesCalls,
      filterByPendingConversation,
      clearFilters,
      isFilteringByAbsencesCalls,
      isFilteringByPendingConversation,
      isFiltering,

      onScrollToBottom,

      showMessagingFilter,
    }
  },
}
</script>

<style scoped>
.spinner-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 999;
}

.b-dropdown-text {
  font-weight: bold;
  color: #676d7d;
}

.empty-state {
  padding: 20px;
  font-style: italic;
}

.search-help-icon:hover {
  color: #16b5ea !important;
}

.popover {
  max-width: none;
}

.search-help-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.85rem;
}

.search-help-list li {
  padding: 0.2rem 0;
  white-space: nowrap;
}

.search-help-list strong {
  color: #16b5ea;
}
</style>
